import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

const UpgradePromptDialog = ({
  open,
  onClose,
  onUpgrade,
  limitType = 'feature',
  currentPlan = 'free',
  suggestedPlan = 'pro'
}) => {
  const [plans, setPlans] = useState({});

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/plans/active`, {
          signal: abortController.signal
        });
        
        if (response.ok && isMounted) {
          const plansData = await response.json();
          // Convert array to object keyed by plan name
          const plansMap = plansData.reduce((acc, plan) => {
            acc[plan.name] = plan;
            return acc;
          }, {});
          setPlans(plansMap);
        }
      } catch (error) {
        // Silently ignore cancelled requests
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED') {
          return;
        }
        if (isMounted) {
          console.error('Error fetching plans:', error);
        }
      }
    };

    fetchPlans();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const planFeatures = {
    basic: {
      name: 'Basic',
      price: plans.basic ? `$${parseFloat(plans.basic.price_per_user_monthly || plans.basic.price_monthly).toFixed(0)}/user` : '$9/user',
      features: [
        '25 projects',
        '50 surveys per month',
        '50 social posts per month',
        '10 GB storage',
        '100 AI messages/month',
        'Unlimited team members',
        'Social media integration',
        'Feature Management & Roadmaps'
      ]
    },
    pro: {
      name: 'Pro',
      price: plans.pro ? `$${parseFloat(plans.pro.price_per_user_monthly).toFixed(0)}/user` : '$18/user',
      features: [
        '50 projects',
        'Unlimited surveys',
        'Unlimited social posts',
        '100 GB storage',
        '300 AI messages/month',
        'Atlassian/Jira integration',
        'Everything in Basic, plus:',
        'Advanced analytics',
        'API access'
      ]
    },
    premium: {
      name: 'Pro',
      price: plans.pro ? `$${parseFloat(plans.pro.price_per_user_monthly).toFixed(0)}/user` : '$18/user',
      features: [
        '50 projects',
        'Unlimited surveys',
        'Unlimited social posts',
        '100 GB storage',
        '300 AI messages/month',
        'Atlassian/Jira integration',
        'Everything in Basic, plus:',
        'Advanced analytics',
        'API access'
      ]
    }
  };

  const limitMessages = {
    projects: {
      title: 'Project Limit Reached',
      message: `You've reached the maximum number of projects for your ${currentPlan} plan.`
    },
    surveys: {
      title: 'Survey Limit Reached',
      message: `You've used all surveys for this month on your ${currentPlan} plan.`
    },
    team_members: {
      title: 'Team Member Limit Reached',
      message: `You've reached the maximum number of team members for your ${currentPlan} plan.`
    },
    feature: {
      title: 'Pro Feature',
      message: `This feature is available on the Pro plan.`
    }
  };

  const limit = limitMessages[limitType] || limitMessages.feature;
  const plan = planFeatures[suggestedPlan] || planFeatures.basic;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {limit.title}
          </Typography>
          <Button 
            onClick={onClose} 
            size="small" 
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          {limit.message}
        </Typography>

        <Box sx={{ my: 3, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {plan.name}
            </Typography>
            <Chip label="Recommended" color="primary" size="small" />
          </Box>
          
          <Typography variant="h4" color="primary" fontWeight="bold">
            {plan.price}
            <Typography component="span" variant="body2" color="text.secondary">
              /month
            </Typography>
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          What's included:
        </Typography>

        <List dense>
          {plan.features.map((feature, index) => (
            <ListItem key={index} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={feature}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: feature.includes(':') ? 'bold' : 'normal'
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 <strong>Tip:</strong> You can cancel anytime. Your plan will remain active until the end of your billing period.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Maybe Later
        </Button>
        <Button 
          onClick={() => onUpgrade(suggestedPlan)} 
          variant="contained" 
          size="large"
        >
          Upgrade to {plan.name}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePromptDialog;
